<script setup lang="ts">
import { useCloned, useDebounceFn } from '@vueuse/core'

import LitepieDatepicker from 'litepie-datepicker-tw3'

import {
  Check,
  ChevronDown,
  ListMinus,
  ListPlus,
  Loader2,
  MapPin,
  MessageSquare,
  Minus,
  Plus,
  QrCode,
  Search,
  Shield,
  SlidersHorizontal,
  Trash2,
  X
} from 'lucide-vue-next'
import axios from 'axios'
import { Checkbox } from '@/ui/checkbox'

import QrCodeScanner from '@/components/QrCodeScanner.vue'
import BulkOptinCombobox from './Components/BulkOptinCombobox.vue'
import type { BulkOptinOption } from './Components/BulkOptinCombobox.vue'
import { resolveAudienceScopedStoreIds, resolveScopedStoreIds } from '@/utils/audienceStoreIds'
import CustomerModal from './Components/CustomerModal.vue'
import CustomerExportCsvButton from './Components/CustomerExportCsvButton.vue'

import type {
  CustomerListCountResponse,
  CustomerListTable,
  CustomerTag,
  RowTableCustomerList,
  RowTagTableCustomerList
} from '@/types/types'
import { defaultFilter } from '@/types/types'

const { fetchMerchantAudiences, segmentTypes } = useAudience()
const { permissions, stores, hasWallet, user, isSupportConnection } = useLocalStorage()
const { formatDate } = useDate()
const {
  checkCustomerFormErrors,
  customerErrors,
  gendersList,
  getMerchantAttributes,
  getTagBy,
  merchantAttributes,
  resetCustomerErrors,
  setFormErrors,
  updateCustomSegments
} = useCustomer()
const { removeNull, areEquals, hasNonNullValue } = useObject()
const { selectedStoreFilter } = useStore()
const { send } = useEvent()
const { t } = useI18n()
const { formatNumber } = useNumber()
const notification = useNotification()
const route = useRoute()
const router = useRouter()

const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const resolvedStoreFilter = computed(() => resolveScopedStoreIds(storeFilter.value, stores.value.length))
const showQrCodeScanner = ref(false)
const merchantSegments = ref([])
const staticMerchantSegments = ref([])

const defaultCustomerTable = {
  sort: {
    sortBy: 'lastValidCheckinDate',
    sortOrder: 'DESC'
  },
  pagination: {
    currentPage: 1
  },
  columns: {
    selection: {
      id: 'selection',
      label: '',
      sortable: false,
      align: 'center',
      headerClass: 'w-12 px-3 sm:px-4'
    },
    customer: {
      id: 'phone',
      label: t('pages.customer.customerPage.tables.thead.contact')
    },
    lastCheckin: {
      id: 'lastValidCheckinDate',
      label: t('pages.customer.customerPage.tables.thead.lastCheckin')
    },
    checkins: {
      id: 'checkinCount',
      label: t('pages.customer.customerPage.tables.thead.checkins'),
      numeric: true
    },
    points: {
      id: 'points',
      label: t('pages.customer.customerPage.tables.thead.points'),
      sortable: true,
      numeric: true
    },
    audience: {
      id: 'audience',
      label: t('pages.customer.customerPage.tables.thead.audience')
    }
  }
} as CustomerListTable

const litepieRegisterDate = ref(null)
const litepieLastCheckinDate = ref(null)
const isLoaded = ref(false)
const isTableLoaded = ref(false)
const isCustomerModalOpen = ref(false)
const currentFilters = ref(defaultFilter)
const countCustomersFiltered = ref(null)
const countCustomersTotal = ref(null)
const filters = ref({ ...defaultFilter })
const formatter = ref({
  date: 'DD/MM/YYYY',
  month: 'MMM'
})

const selectedAttributes = ref()

const defaultCustomer = {
  birthday: null,
  birthdayFormatted: null,
  city: null,
  country: null,
  email: '',
  loyaltyCardNumber: null,
  firstname: '',
  gender: gendersList[0].id,
  hasCheckin: false,
  hasOptinEmail: null,
  lastname: '',
  lists: [],
  nationalNumber: null,
  optin: null,
  phone: null,
  points: 0,
  postalCode: null,
  referenceStore: null,
  tags: []
}

const newCustomer = ref(useCloned(defaultCustomer).cloned)
const showUserImportButton = computed(() => permissions.value['import.create'])
const displayQrCodeScanner = computed(() => hasWallet.value)

const searchText = ref('')
const table = ref(defaultCustomerTable)
const selectedSegments = ref([])
// Read-only: narrows which store options are shown once an audience is picked. Never mutates
// `storeFilter` — `merchantSegments` is already server-side scoped to `storeFilter` (see
// `resolvedStoreFilter` above), so mutating it here would refetch audiences on every audience
// selection and could create a store↔audience feedback loop.
const storesScopedByAudience = computed(() =>
  resolveAudienceScopedStoreIds(merchantSegments.value.filter((segment) => selectedSegments.value.includes(segment.id)))
)

const RefThePageSettings = ref(null)

let customersCancelTokenSource = null

const pageTitle = {
  title: 'pages.customer.customerPage.title'
}

const filtered = computed(() => {
  return !areEquals(currentFilters.value, defaultFilter)
})

const showUserCreateButton = computed(() => permissions.value['customer.create'])
const showUserExportButton = computed(() => permissions.value['customer.export'])

const selectedCustomerIds = ref<Set<number>>(new Set())
const isBulkDeleteModalOpen = ref(false)
const isBulkOptinModalOpen = ref(false)
const isBulkReferenceStoreModalOpen = ref(false)
const isBulkAddToListModalOpen = ref(false)
const isBulkRemoveFromListModalOpen = ref(false)
const isBulkActionLoading = ref(false)
const isBulkListRefreshing = ref(false)
const bulkSmsOptinId = ref<number | null>(null)
const bulkEmailOptinId = ref<number | null>(null)
const bulkPushOptinId = ref<number | null>(null)
const bulkReferenceStoreId = ref<number | null>(null)
const bulkListSegmentId = ref<number | null>(null)
const bulkRemoveListSegmentId = ref<number | null>(null)
const BULK_ACTION_CHUNK_SIZE = 100
const BULK_LIST_REFRESH_DELAY_MS = 3000

type BulkOptinChannel = 'sms' | 'email' | 'push'
type BulkOptinValue = boolean | null

const selectedCustomerCount = computed(() => selectedCustomerIds.value.size)
const hasSelectedCustomers = computed(() => selectedCustomerCount.value > 0)
const canUseBulkActions = computed(() => isSupportConnection.value)
const tableColumns = computed(() => {
  if (canUseBulkActions.value) return table.value.columns

  const { selection: _selection, ...columnsWithoutSelection } = table.value.columns
  return columnsWithoutSelection
})
const currentPageCustomerIds = computed(() => (table.value.rows ?? []).map((row) => row.id))
const bulkReferenceStoreOptions = computed(() =>
  stores.value.map((store) => ({ id: store.id, text: store.name })).sort((a, b) => a.text.localeCompare(b.text))
)

const bulkSmsOptinOptions = computed<BulkOptinOption[]>(() => [
  {
    id: 1,
    text: t('pages.customer.customerPage.blocks.create.fields.optin.false.description'),
    value: false,
    badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
    badgeColor: 'red',
    badgeText: t('pages.customer.customerDetailPage.sms')
  },
  {
    id: 2,
    text: t('pages.customer.customerPage.blocks.create.fields.optin.true.description'),
    value: true,
    badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
    badgeColor: 'green',
    badgeText: t('pages.customer.customerDetailPage.sms')
  },
  {
    id: 3,
    text: t('pages.customer.customerPage.blocks.create.fields.optin.unknown.description'),
    value: null,
    badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
    badgeColor: 'gray',
    badgeText: t('pages.customer.customerDetailPage.sms')
  }
])

const bulkEmailOptinOptions = computed<BulkOptinOption[]>(() => [
  {
    id: 1,
    text: t('pages.customer.customerPage.blocks.create.fields.has_optin_email.false.description'),
    value: false,
    badgeIcon: 'HeroEnvelopeIconOutline',
    badgeColor: 'red',
    badgeText: t('pages.customer.customerDetailPage.email')
  },
  {
    id: 2,
    text: t('pages.customer.customerPage.blocks.create.fields.has_optin_email.true.description'),
    value: true,
    badgeIcon: 'HeroEnvelopeIconOutline',
    badgeColor: 'green',
    badgeText: t('pages.customer.customerDetailPage.email')
  },
  {
    id: 3,
    text: t('pages.customer.customerPage.blocks.create.fields.has_optin_email.unknown.description'),
    value: null,
    badgeIcon: 'HeroEnvelopeIconOutline',
    badgeColor: 'gray',
    badgeText: t('pages.customer.customerDetailPage.email')
  }
])

const bulkPushOptinOptions = computed<BulkOptinOption[]>(() => [
  {
    id: 1,
    text: t('pages.customer.customerPage.bulkActions.optin.push.false'),
    value: false,
    badgeIcon: 'HeroBellIconOutline',
    badgeColor: 'red',
    badgeText: t('pages.customer.customerDetailPage.wallet.push')
  },
  {
    id: 2,
    text: t('pages.customer.customerPage.bulkActions.optin.push.true'),
    value: true,
    badgeIcon: 'HeroBellIconOutline',
    badgeColor: 'green',
    badgeText: t('pages.customer.customerDetailPage.wallet.push')
  }
])

const hasBulkOptinSelection = computed(
  () => bulkSmsOptinId.value !== null || bulkEmailOptinId.value !== null || bulkPushOptinId.value !== null
)

const headerCheckboxState = computed(() => {
  const pageIds = currentPageCustomerIds.value
  if (!pageIds.length) return false

  const selectedOnPageCount = pageIds.filter((id) => selectedCustomerIds.value.has(id)).length
  if (selectedOnPageCount === 0) return false
  if (selectedOnPageCount === pageIds.length) return true
  return 'indeterminate'
})

const isCustomerSelected = (customerId: number) => selectedCustomerIds.value.has(customerId)

const toggleCustomerSelection = (customerId: number, checked: boolean | 'indeterminate') => {
  const next = new Set(selectedCustomerIds.value)
  if (checked === true) {
    next.add(customerId)
  } else {
    next.delete(customerId)
  }
  selectedCustomerIds.value = next
}

const toggleSelectAllOnPage = (checked: boolean | 'indeterminate') => {
  const next = new Set(selectedCustomerIds.value)
  if (checked === true) {
    currentPageCustomerIds.value.forEach((id) => next.add(id))
  } else {
    currentPageCustomerIds.value.forEach((id) => next.delete(id))
  }
  selectedCustomerIds.value = next
}

const clearCustomerSelection = () => {
  selectedCustomerIds.value = new Set()
}

watch(canUseBulkActions, (enabled) => {
  if (!enabled) clearCustomerSelection()
})

const chunkIds = (ids: number[], size: number) => {
  const chunks: number[][] = []
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size))
  }
  return chunks
}

const runBulkAction = async ({
  request,
  requests,
  successKey,
  errorKey,
  onSuccess,
  getProcessedCount,
  skippedKey,
  refreshDelayMs = 0
}: {
  request?: (chunk: number[]) => Promise<unknown>
  requests?: Array<(chunk: number[]) => Promise<unknown>>
  successKey: string
  errorKey: string
  onSuccess?: () => void
  getProcessedCount?: (response: unknown) => number
  skippedKey?: string
  refreshDelayMs?: number
}) => {
  const handlers = requests ?? (request ? [request] : [])
  if (!canUseBulkActions.value || !handlers.length || !hasSelectedCustomers.value || isBulkActionLoading.value) {
    return
  }

  isBulkActionLoading.value = true
  const ids = Array.from(selectedCustomerIds.value)

  try {
    let processedCount = 0
    for (const handler of handlers) {
      for (const chunk of chunkIds(ids, BULK_ACTION_CHUNK_SIZE)) {
        const response = await handler(chunk)
        processedCount += getProcessedCount ? getProcessedCount(response) : chunk.length
      }
    }

    // The API may silently skip some customers (e.g. outside a list's stores): report what
    // actually happened instead of the selection size.
    const skippedCount = getProcessedCount ? ids.length - processedCount : 0

    if (processedCount > 0 || skippedCount === 0) {
      notification.create('success', {
        text: t(successKey, processedCount)
      })
    }
    if (skippedCount > 0 && skippedKey) {
      notification.create('error', {
        text: t(skippedKey, { count: skippedCount }, skippedCount)
      })
    }

    clearCustomerSelection()
    isBulkActionLoading.value = false
    onSuccess?.()

    table.value.pagination.currentPage = 1
    if (refreshDelayMs > 0) {
      isBulkListRefreshing.value = true
      isTableLoaded.value = false
      try {
        await new Promise((resolve) => setTimeout(resolve, refreshDelayMs))
        await initCustomersCountRequest()
      } finally {
        isBulkListRefreshing.value = false
      }
    } else {
      await initCustomersCountRequest()
    }
  } catch {
    notification.create('error', {
      text: t(errorKey)
    })
  } finally {
    isBulkActionLoading.value = false
  }
}

const openBulkOptinModal = () => {
  if (!canUseBulkActions.value) return
  bulkSmsOptinId.value = null
  bulkEmailOptinId.value = null
  bulkPushOptinId.value = null
  isBulkOptinModalOpen.value = true
}

const closeBulkOptinModal = () => {
  if (isBulkActionLoading.value) return
  isBulkOptinModalOpen.value = false
  bulkSmsOptinId.value = null
  bulkEmailOptinId.value = null
  bulkPushOptinId.value = null
}

const openBulkReferenceStoreModal = () => {
  if (!canUseBulkActions.value) return
  bulkReferenceStoreId.value = stores.value.length === 1 ? stores.value[0].id : null
  isBulkReferenceStoreModalOpen.value = true
}

const closeBulkReferenceStoreModal = () => {
  if (isBulkActionLoading.value) return
  isBulkReferenceStoreModalOpen.value = false
  bulkReferenceStoreId.value = null
}

const openBulkAddToListModal = () => {
  if (!canUseBulkActions.value) return
  bulkListSegmentId.value = null
  isBulkAddToListModalOpen.value = true
}

const closeBulkAddToListModal = () => {
  if (isBulkActionLoading.value) return
  isBulkAddToListModalOpen.value = false
  bulkListSegmentId.value = null
}

const openBulkRemoveFromListModal = () => {
  if (!canUseBulkActions.value) return
  bulkRemoveListSegmentId.value = null
  isBulkRemoveFromListModalOpen.value = true
}

const closeBulkRemoveFromListModal = () => {
  if (isBulkActionLoading.value) return
  isBulkRemoveFromListModalOpen.value = false
  bulkRemoveListSegmentId.value = null
}

const refreshStaticMerchantSegments = async () => {
  const audiences = await fetchMerchantAudiences({
    pagination: { pageSize: 1000, page: 1 },
    storeIds: resolvedStoreFilter.value
  })
  staticMerchantSegments.value = audiences.data.filter((audience) => audience.type === 'static')
  merchantSegments.value = audiences.data
  lastSegmentsPage.value = audiences.meta.last_page
}

const deleteSelectedCustomers = async () => {
  await runBulkAction({
    request: (chunk) =>
      axios.delete('customers', {
        data: { customerMerchantIds: chunk }
      }),
    successKey: 'pages.customer.customerPage.bulkActions.delete.success',
    errorKey: 'pages.customer.customerPage.bulkActions.delete.error',
    onSuccess: () => {
      isBulkDeleteModalOpen.value = false
    }
  })
}

const getBulkOptinUpdates = () => {
  const updates: Array<{ channel: BulkOptinChannel; hasOptin: BulkOptinValue }> = []

  if (bulkSmsOptinId.value !== null) {
    const option = bulkSmsOptinOptions.value.find((item) => item.id === bulkSmsOptinId.value)
    if (option) updates.push({ channel: 'sms', hasOptin: option.value })
  }

  if (bulkEmailOptinId.value !== null) {
    const option = bulkEmailOptinOptions.value.find((item) => item.id === bulkEmailOptinId.value)
    if (option) updates.push({ channel: 'email', hasOptin: option.value })
  }

  if (bulkPushOptinId.value !== null) {
    const option = bulkPushOptinOptions.value.find((item) => item.id === bulkPushOptinId.value)
    if (option) updates.push({ channel: 'push', hasOptin: option.value })
  }

  return updates
}

const updateSelectedCustomersOptin = async () => {
  const updates = getBulkOptinUpdates()
  if (!updates.length) return

  await runBulkAction({
    requests: updates.map(
      (update) => (chunk) =>
        axios.patch('customers/bulk/optin', {
          customerMerchantIds: chunk,
          channel: update.channel,
          hasOptin: update.hasOptin
        })
    ),
    successKey: 'pages.customer.customerPage.bulkActions.optin.success',
    errorKey: 'pages.customer.customerPage.bulkActions.optin.error',
    onSuccess: closeBulkOptinModal
  })
}

const updateSelectedCustomersReferenceStore = async () => {
  const referenceStoreId = bulkReferenceStoreId.value
  if (!referenceStoreId) return

  await runBulkAction({
    request: (chunk) =>
      axios.patch('customers/bulk/reference-store', {
        customerMerchantIds: chunk,
        referenceStoreId
      }),
    successKey: 'pages.customer.customerPage.bulkActions.referenceStore.success',
    errorKey: 'pages.customer.customerPage.bulkActions.referenceStore.error',
    onSuccess: closeBulkReferenceStoreModal
  })
}

const addSelectedCustomersToList = async () => {
  if (!bulkListSegmentId.value || !user.value?.merchant?.id) return

  const merchantId = user.value.merchant.id
  const segmentId = bulkListSegmentId.value

  await runBulkAction({
    request: (chunk) =>
      axios.post(`merchants/${merchantId}/segments/${segmentId}/customers`, {
        customer_merchant_ids: chunk
      }),
    successKey: 'pages.customer.customerPage.bulkActions.addToList.success',
    errorKey: 'pages.customer.customerPage.bulkActions.addToList.error',
    onSuccess: closeBulkAddToListModal,
    getProcessedCount: (response) => (response as { data?: { data?: { count?: number } } })?.data?.data?.count ?? 0,
    skippedKey: 'pages.customer.customerPage.bulkActions.addToList.skippedOutOfScope',
    refreshDelayMs: BULK_LIST_REFRESH_DELAY_MS
  })
}

const removeSelectedCustomersFromList = async () => {
  if (!bulkRemoveListSegmentId.value || !user.value?.merchant?.id) return

  const merchantId = user.value.merchant.id
  const segmentId = bulkRemoveListSegmentId.value

  await runBulkAction({
    request: (chunk) =>
      axios.delete(`merchants/${merchantId}/segments/${segmentId}/customers`, {
        data: { customer_merchant_ids: chunk }
      }),
    successKey: 'pages.customer.customerPage.bulkActions.removeFromList.success',
    errorKey: 'pages.customer.customerPage.bulkActions.removeFromList.error',
    refreshDelayMs: BULK_LIST_REFRESH_DELAY_MS,
    onSuccess: closeBulkRemoveFromListModal
  })
}

const currentSegmentsPage = ref(1)
const lastSegmentsPage = ref(null)
const isSegmentListLoading = ref(false)
const isFiltersModalOpen = ref(false)
const handleLoadAudiences = async (text?: string) => {
  if (isSegmentListLoading.value || (lastSegmentsPage.value && currentSegmentsPage.value >= lastSegmentsPage.value)) {
    return
  }

  try {
    isSegmentListLoading.value = true
    currentSegmentsPage.value++

    const audiences = await fetchMerchantAudiences({
      pagination: {
        pageSize: 15,
        page: currentSegmentsPage.value
      },
      search: text || '',
      type: 'static',
      storeIds: resolvedStoreFilter.value
    })

    staticMerchantSegments.value = [
      ...staticMerchantSegments.value,
      ...audiences.data.filter(
        (newSegment) => !staticMerchantSegments.value.some((existingSegment) => existingSegment.id === newSegment.id)
      )
    ]

    lastSegmentsPage.value = audiences.meta.last_page
  } catch {
    currentSegmentsPage.value--
  } finally {
    isSegmentListLoading.value = false
  }
}

const handleSearchAudience = async (text: string) => {
  try {
    isSegmentListLoading.value = true
    currentSegmentsPage.value = 1

    const searchedAudiences = await fetchMerchantAudiences({
      search: text,
      type: 'static',
      pagination: {
        pageSize: 15,
        page: 1
      },
      storeIds: resolvedStoreFilter.value
    })

    merchantSegments.value = searchedAudiences.data
    lastSegmentsPage.value = searchedAudiences.meta.last_page
  } finally {
    isSegmentListLoading.value = false
  }
}

const search = useDebounceFn(async (value?: string) => {
  if (typeof value === 'string') {
    if (value !== '' && value.length < 3) {
      return
    }

    table.value.pagination.currentPage = 1
  }

  isTableLoaded.value = false

  router.push({
    path: '/customer',
    query: {
      ...getSelectedFiltersParams(),
      page: table.value.pagination.currentPage
    },
    replace: true
  })

  await fetchTable()
}, 200)

const setSegmentsFilter = () => {
  if (filters.value.segmentIds) {
    const segmentIds = Array.isArray(filters.value.segmentIds) ? filters.value.segmentIds : [filters.value.segmentIds]

    selectedSegments.value = segmentIds.map((x) => Number(x))

    const missingSegments = selectedSegments.value.filter(
      (id) => !merchantSegments.value.some((segment) => segment.id === id)
    )

    if (missingSegments) {
      fetchMerchantAudiences({
        pagination: {
          pageSize: missingSegments.length,
          page: 1
        },
        type: 'static',
        search: missingSegments.join(','),
        storeIds: resolvedStoreFilter.value
      }).then((response) => {
        if (response?.data?.length) {
          merchantSegments.value.push(response.data)
        }
      })
    }
  } else {
    selectedSegments.value = []
  }
}

const createCustomerIsLoading = reactive({
  redirect: false,
  create: false
})
const addCustomerAndRedirect = async () => {
  try {
    createCustomerIsLoading.redirect = true
    const customerId = await createCustomer()
    router.push({ name: 'CustomerDetail', params: { customerId } })
  } finally {
    createCustomerIsLoading.redirect = false
  }
}
const addCustomerAndCreateANewOne = async () => {
  try {
    createCustomerIsLoading.create = true
    await createCustomer()
    resetNewCustomerForm()
    isCustomerModalOpen.value = true
  } finally {
    createCustomerIsLoading.create = false
  }
}

const createCustomer = async () => {
  if (!createCustomerIsLoading.create || !checkCustomerFormErrors(newCustomer.value)) {
    const requestParams = {
      phone: newCustomer.value.phone?.replace(/\s/g, ''),
      referenceStoreId: newCustomer.value.referenceStore?.id,
      country: newCustomer.value.country,
      firstName: newCustomer.value.firstname,
      lastName: newCustomer.value.lastname,
      email: newCustomer.value.email,
      loyaltyCardNumber: newCustomer.value.loyaltyCardNumber,
      gender: newCustomer.value.gender
        ? gendersList.find((gender) => gender.id === newCustomer.value.gender).slug
        : null,
      birthday: newCustomer.value.birthdayFormatted,
      hasOptin: newCustomer.value.optin,
      hasOptinEmail: newCustomer.value.hasOptinEmail,
      postalCode: newCustomer.value.postalCode,
      city: newCustomer.value.city,
      hasCheckin: false,
      source: 'added_manually_from_dashboard',
      points: 0
    }

    try {
      const res = await axios.post('customers/withPoints', requestParams)
      const { data } = res
      const id = data?.data?.id

      notification.create('success', {
        text: t('pages.customer.customerPage.notifications.create.success')
      })

      if (hasNonNullValue(selectedAttributes.value)) await saveCustomerAttributes(id)
      if (newCustomer.value.lists.length > 0) await saveMerchantSegments(id)

      if (newCustomer.value.tags.length > 0) {
        await saveCustomerTags(id)
      } else {
        await fetchTable()
      }

      return id
    } catch (e) {
      if (e.response.data) setFormErrors(e.response.data)

      if (e.response.data?.message) {
        if (e.response.status === 409) {
          router.push({ name: 'CustomerDetail', params: { customerId: e.response.data.customerId } })
        }
        if (e.response.status === 422) {
          const isBirthdayError = e.response.data.errors?.birthday
          notification.create('error', {
            text: isBirthdayError
              ? t('pages.customer.customerPage.notifications.create.error')
              : e.response.data.message
          })
        }
      }

      throw new Error(e)
    }
  } else {
    throw new Error('Form is not valid')
  }
}
const saveCustomerTags = async (customerId: number) => {
  try {
    await axios.post(`customers/${customerId}/tags`, {
      tagIds: newCustomer.value.tags
    })
    newCustomer.value.tags = []
    await fetchTable()
  } catch {
    notification.create('error', {
      text: t('notifications.save.error'),
      timeout: 5000
    })
    throw new Error('Rate limit error')
  }
}

const saveMerchantSegments = async (customerId: number) => {
  if (!customerId || typeof customerId !== 'number') {
    throw new Error('Invalid customer ID')
  }

  try {
    await updateCustomSegments(newCustomer)

    const segmentIds = newCustomer.value.lists.map((s) => s.id).filter(Boolean)

    if (segmentIds.length > 0) {
      await axios.put(`customers/${customerId}/segments`, { segmentIds })
    }

    newCustomer.value.lists = []
  } catch (error) {
    notification.create('error', {
      text: t('notifications.save.error'),
      timeout: 5000
    })

    if (error.response && error.response.status === 429) {
      throw new Error('Rate limit exceeded')
    } else {
      throw error
    }
  }
}

const saveCustomerAttributes = async (customerId: number) => {
  const requests = Object.entries(selectedAttributes.value)
    .filter(([, value]) => value !== null)
    .map(([key, value]) =>
      axios.post(`customers/${customerId}/attributes`, {
        attributeId: key,
        value: value
      })
    )

  await Promise.all(requests)
}

const resetNewCustomerForm = () => {
  isCustomerModalOpen.value = false
  populateMerchantAttributes()
  resetCustomerErrors()
  newCustomer.value = useCloned(defaultCustomer).cloned.value
}

const handleScanningSession = () => {
  showQrCodeScanner.value = true
}

const handleCloseQrCodeScanner = () => {
  showQrCodeScanner.value = false
}

const openImport = async () => {
  isCustomerModalOpen.value = false
  router.push({ name: 'Import' })
}

const openCreateCustomer = () => {
  isCustomerModalOpen.value = true
  send('segment', 'customer:create:clicked')
}

const changePage = async (page) => {
  table.value.pagination.currentPage = page
  router.push({
    path: '/customer',
    query: {
      ...getSelectedFiltersParams(),
      page
    }
  })
  isTableLoaded.value = false
  await search()
}
const changeSort = async (row) => {
  isTableLoaded.value = false
  table.value.sort = row
  resetSearch()
}

const getFiltersFromUrl = () => {
  filters.value = mergeFilters(route.query)
  currentFilters.value = { ...filters.value }
  table.value.pagination.currentPage = route.query.page ? parseInt(route.query.page.toString()) : null
  searchText.value = route.query.search ? route.query.search.toString() : ''
}

const resetSearch = () => {
  table.value.pagination.currentPage = 1
  search()
}

watch(
  () => route.query,
  () => getFiltersFromUrl(),
  { deep: true }
)

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  table.value.pagination.currentPage = 1
  currentSegmentsPage.value = 1
  await Promise.all([fetchData(), refreshStaticMerchantSegments()])
})

const normalizePostalCode = (postalCode: string): string | null => {
  const normalized = postalCode.replace(/\s/g, '').replace(/\*+$/, '')
  return normalized.length >= 2 ? normalized : null
}

const postalCodeInput = ref('')
const postalCodeFieldRef = ref<HTMLInputElement | null>(null)

const addPostalCodeFromInput = () => {
  const code = normalizePostalCode(postalCodeInput.value)
  if (!code) return
  if (!filters.value.postalCodes.includes(code)) {
    filters.value.postalCodes = [...filters.value.postalCodes, code]
  }
  postalCodeInput.value = ''
}

const removePostalCode = (code: string) => {
  filters.value.postalCodes = filters.value.postalCodes.filter((value) => value !== code)
}

const onPostalCodeKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault()
    addPostalCodeFromInput()
    return
  }
  if (event.key === 'Backspace' && postalCodeInput.value === '' && filters.value.postalCodes.length > 0) {
    removePostalCode(filters.value.postalCodes[filters.value.postalCodes.length - 1])
  }
}

const focusPostalCodeInput = () => {
  postalCodeFieldRef.value?.focus()
}

const getFiltersParams = () => {
  return {
    sortBy: table.value.sort.sortBy,
    sortOrder: table.value.sort.sortOrder,
    storeIds:
      storeFilter.value.length > 0 && storeFilter.value.length < stores.value.length
        ? storeFilter.value.join(',')
        : null,
    search: searchText.value,
    fromDate: filters.value.registerDate.from,
    toDate: filters.value.registerDate.to,
    lastCheckinFromDate: filters.value.lastCheckinDate.from,
    lastCheckinToDate: filters.value.lastCheckinDate.to,
    genders: filters.value.genders,
    fromAge: filterByAge() ? filters.value.ages.value[0] : null,
    toAge: filterByAge() ? filters.value.ages.value[1] : null,
    hasOptin: filters.value.hasOptin,
    tags: filters.value.tags,
    segmentIds: selectedSegments?.value?.join(',') || null,
    postalCodes: filters.value.postalCodes.length > 0 ? filters.value.postalCodes : null
  }
}
const getSelectedFiltersParams = () => {
  const selectedFilter = getFiltersParams()
  return removeNull(selectedFilter)
}
const mergeFilters = (query) => {
  return {
    ages: {
      min: 0,
      max: 100,
      value: [query.fromAge ? query.fromAge : 0, query.toAge ? query.toAge : 100]
    },
    genders: query.genders ? [].concat(query.genders) : [],
    hasOptin: query.hasOptin ? [].concat(query.hasOptin) : [],
    lastCheckinDate: {
      from: query.lastCheckinFromDate || '',
      to: query.lastCheckinToDate || ''
    },
    registerDate: {
      from: query.fromDate || '',
      to: query.toDate || ''
    },
    tags: query.tags ? [].concat(query.tags).map(Number) : [],
    segmentIds: query.segmentIds ? JSON.parse(`[${query.segmentIds}]`) : [],
    postalCodes: query.postalCodes ? [].concat(query.postalCodes).map(String) : []
  }
}

const fetchTable = async () => {
  if (customersCancelTokenSource) {
    customersCancelTokenSource.cancel('Operation canceled by the user.')
  }

  isTableLoaded.value = false

  const currentTokenSource = axios.CancelToken.source()
  customersCancelTokenSource = currentTokenSource

  let filtersParams = getFiltersParams()
  const addDatesFromFilter = (res) => {
    res.fromDate = filtersParams.fromDate
    res.toDate = filtersParams.toDate
    res.lastCheckinFromDate = filtersParams.lastCheckinFromDate
    res.lastCheckinToDate = filtersParams.lastCheckinToDate
    return res
  }
  const params = removeNull({
    ...convertDates(filtersParams, 'DD/MM/YYYY', 'YYYY-MM-DD'),
    isChannel: 1,
    pageSize: 10,
    page: table.value.pagination.currentPage
  })

  try {
    const requestTableRes = await axios.get('customers', { params, cancelToken: currentTokenSource.token })

    if (requestTableRes.data && requestTableRes.data.data.length === 1 && searchText.value.startsWith('cust_')) {
      router.push({ name: 'CustomerDetail', params: { customerId: requestTableRes.data.data[0].id } })
    }
    if (requestTableRes.data) {
      convertDates(addDatesFromFilter(requestTableRes.data), 'YYYY-MM-DD', 'DD/MM/YYYY')
      countCustomersFiltered.value = requestTableRes.data.meta.total
      let rows = [] as RowTableCustomerList[]
      isLoaded.value = true
      isTableLoaded.value = true
      requestTableRes.data.data.forEach((row) => {
        const audiences = [] as RowTagTableCustomerList[]

        if (row.segments.length > 0) {
          row.segments.forEach((segment) => {
            const segmentType = segment.frequency_type ? 'typology' : segment.type
            const segmentTypeConfig = segmentTypes.find((type) => type.id === segmentType)

            audiences.push({
              id: segment.id,
              label: segment.name,
              type: segmentType,
              color: segmentTypeConfig.color,
              icon: segmentTypeConfig.icon
            })
          })
        }

        const displayedName = computed(() => {
          if (row.firstName || row.lastName) {
            let parts = []
            if (row.firstName) parts.push(row.firstName)
            if (row.lastName) parts.push(row.lastName)
            return parts.join(' ').slice(0, 20)
          }
          return null
        })
        let lastCheckin = {}

        if (row.lastValidCheckinDate === null) {
          lastCheckin = {
            date: '-',
            time: ''
          }
        } else {
          lastCheckin = {
            date: new Date(row.lastValidCheckinDate).toLocaleDateString('fr-FR'),
            time: new Date(row.lastValidCheckinDate).toLocaleTimeString('fr-FR')
          }
        }
        rows.push({
          id: row.id,
          data: {
            customer: {
              id: row.id,
              phone: row.phone,
              name: displayedName.value,
              email: row.email,
              loyaltyCardNumber: row.loyaltyCardNumber,
              gender: row.gender,
              hasOptin: row.hasOptin,
              countryFlag: row.countryFlag
            },
            lastCheckin: lastCheckin,
            checkins: { value: row.checkinCount },
            points: { value: row.points ?? 0 },
            audience: { items: audiences }
          }
        } as RowTableCustomerList)
      })
      table.value.rows = rows
      table.value.pagination = {
        currentPage: requestTableRes.data.meta.current_page,
        lastPage: requestTableRes.data.meta.last_page,
        total: requestTableRes.data.meta.total,
        pageSize: requestTableRes.data.meta.per_page
      }
    }

    return addDatesFromFilter(requestTableRes.data)
  } catch (error) {
    if (!axios.isCancel(error)) {
      throw error
    }
  } finally {
    if (customersCancelTokenSource === currentTokenSource) {
      isTableLoaded.value = true
      customersCancelTokenSource = null
    }
  }
}

const searchedValue = computed(() => searchText.value)
const fetchData = async () => {
  await fetchTable()
  isLoaded.value = true
  isTableLoaded.value = true
}

const tags = computed(() => {
  const channelTags = getTagBy('type', 'channel')
  const marketingTags = getTagBy('type', 'marketing')
  return [...channelTags, ...marketingTags]
})

const filtersUpdateAges = (event) => {
  filters.value.ages.value = event
}
const filterTagBy = (tags: CustomerTag[], filter: string) => {
  return tags.filter((tag) => tag.type === filter)
}
const filterByAge = () => {
  return filters.value.ages.value[0] !== 0 || filters.value.ages.value[1] !== 100
}

const activeFilterTags = computed(() => {
  const result: { key: string; text: string }[] = []

  for (const key of Object.keys(currentFilters.value)) {
    if (JSON.stringify(defaultFilter[key]) === JSON.stringify(currentFilters.value[key])) continue

    const label = t('pages.customer.customerPage.filters.' + key)
    let value = ''

    if (key === 'ages' && currentFilters.value.ages.value) {
      value = currentFilters.value.ages.value.join(' - ')
    } else if (key === 'segmentIds' && currentFilters.value.segmentIds) {
      value = merchantSegments.value
        ?.filter((segment) => selectedSegments.value.includes(segment.id))
        .map((segment) => segment.name)
        .join(', ')
    } else if (key === 'genders' && currentFilters.value.genders) {
      value = currentFilters.value.genders.map((f) => t('global.gender.' + f)).join(' - ')
    } else if (key === 'hasOptin' && currentFilters.value.hasOptin) {
      value = currentFilters.value.hasOptin.map((f) => t('pages.customer.customerPage.filters.optin.' + f)).join(' - ')
    } else if (key === 'tags' && currentFilters.value.tags) {
      value = currentFilters.value.tags
        .map((f) =>
          tags.value.filter((tag) => {
            return tag.id == f
          })
        )
        .map((e) => e[0]?.name)
        .join(' - ')
    } else if (key === 'lastCheckinDate' && currentFilters.value.lastCheckinDate) {
      value = t('pages.customer.customerPage.filters.datesFromTo', {
        from: currentFilters.value.lastCheckinDate.from,
        to: currentFilters.value.lastCheckinDate.to
      })
    } else if (key === 'registerDate' && currentFilters.value.registerDate) {
      value = t('pages.customer.customerPage.filters.datesFromTo', {
        from: currentFilters.value.registerDate.from,
        to: currentFilters.value.registerDate.to
      })
    } else if (key === 'postalCodes' && currentFilters.value.postalCodes) {
      value = currentFilters.value.postalCodes.join(', ')
    }

    result.push({ key, text: `${label} : ${value}` })
  }

  return result
})

const removeFilter = async (filter: string) => {
  isLoaded.value = false
  if (filter === 'ages') {
    filters.value.ages.value = [0, 100]
  } else if (filter === 'lastCheckinDate' || filter === 'registerDate') {
    filters.value[filter] = {
      from: '',
      to: ''
    }
  } else {
    filters.value[filter] = []
  }
  if (filter === 'segmentIds') {
    selectedSegments.value = []
  }
  await applyFilters()
}
const applyFilters = async () => {
  addPostalCodeFromInput()
  isTableLoaded.value = false
  router.push({
    path: '/customer',
    query: {
      ...getSelectedFiltersParams(),
      page: 1
    }
  })
  await fetchData()
}

const displayNoResults = () => {
  return table.value.rows && table.value.rows.length == 0 && (filtered.value === true || searchText.value)
}

const resetFilters = async () => {
  isLoaded.value = false
  RefThePageSettings.value = null
  searchText.value = null
  filters.value = { ...defaultFilter }
  await applyFilters()
}
const showLitepie = (field: string) => {
  if (field === 'register') {
    litepieRegisterDate.value.isShow = !litepieRegisterDate.value.isShow
    litepieLastCheckinDate.value.isShow = false
  } else {
    litepieLastCheckinDate.value.isShow = !litepieLastCheckinDate.value.isShow
    litepieRegisterDate.value.isShow = false
  }
}
const convertDates = (obj, formatFrom, formatTo) => {
  let dateFields = ['fromDate', 'toDate', 'lastCheckinFromDate', 'lastCheckinToDate']
  dateFields.forEach(
    (date) =>
      (obj[date] =
        obj[date] !== '' ? formatDate({ date: obj[date], initialFormat: formatFrom, toFormat: formatTo }) : obj[date])
  )
  return obj
}
const initCustomersCountRequest = async () => {
  const initCustomersCountRequestRes = await axios.get('customers/count', {
    params: {
      pageSize: 1,
      storeIds: null
    }
  })
  let params: CustomerListCountResponse = initCustomersCountRequestRes.data
  if (params.data) {
    countCustomersTotal.value = params.data.count ?? 0
  }
  await fetchData()
}

const handleFilters = (filterModalState) => {
  isFiltersModalOpen.value = filterModalState
  if (filterModalState === true) {
    setSegmentsFilter()
  } else {
    getFiltersFromUrl()
  }
}

const emptyStateButtons = {
  primaryButton: {
    text: t('pages.customer.emptyState.button'),
    icon: 'HeroPlusIconSolid',
    isOutlined: false
  },
  secondaryButton: {
    text: t('pages.customer.emptyState.import'),
    icon: 'HeroArrowDownTrayIconSolid',
    isOutlined: true
  }
}

const handleClickEmptyState = (event: 'primaryButton' | 'secondaryButton') => {
  if (event === 'primaryButton') {
    isCustomerModalOpen.value = true
  }

  if (event === 'secondaryButton') {
    openImport()
  }
}

const queryUrl = computed(() => route.query)

const populateMerchantAttributes = async () => {
  if (merchantAttributes.value.length < 1) {
    await getMerchantAttributes()
  }
  merchantAttributes.value.forEach((attribute) => {
    selectedAttributes.value = {
      ...selectedAttributes.value,
      [attribute.id]: null
    }
  })
}

onMounted(async () => {
  getFiltersFromUrl()
  const audiences = await fetchMerchantAudiences({
    pagination: { pageSize: 1000, page: 1 },
    storeIds: resolvedStoreFilter.value
  })
  staticMerchantSegments.value = audiences.data.filter((audience) => audience.type === 'static')
  merchantSegments.value = audiences.data
  lastSegmentsPage.value = audiences.meta.last_page
  setSegmentsFilter()
  await populateMerchantAttributes()
  if (queryUrl.value.addNewCustomer) {
    isCustomerModalOpen.value = true
  }
  if (queryUrl.value.openImport) {
    await openImport()
  }
  await initCustomersCountRequest()
})
</script>
<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheListHeader
          :title="t(pageTitle.title)"
          :count="{ filtered: countCustomersFiltered ?? 0, total: countCustomersTotal ?? 0 }"
          unit-key="global.words.customer"
        >
          <template #actions>
            <Button
              v-if="isSupportConnection"
              variant="outline"
              @click="router.push({ name: 'FraudSuspectCustomers' })"
            >
              <Shield class="size-4" />
              {{ t('pages.customer.fraudSuspects.title') }}
            </Button>

            <Button v-if="showUserImportButton" variant="outline" @click="openImport">
              {{ t('pages.import.importPage.create.button') }}
            </Button>

            <Button
              v-if="showUserCreateButton"
              id="createCustomerButton"
              variant="default-primary"
              @click="openCreateCustomer"
            >
              <Plus />
              {{ t('pages.customer.customerPage.cta.create') }}
            </Button>
          </template>
        </TheListHeader>

        <ThePageSettings
          ref="RefThePageSettings"
          :display-date-range-picker="false"
          display-search
          :searched-value="searchedValue"
          @show-scanner="handleScanningSession"
          @validate-filters="applyFilters"
          @update:search-field="search"
          @handle-filters-modal="handleFilters"
        >
          <template #filtersDescription>
            {{ t('pages.customer.customerPage.filters.description') }}
          </template>
          <template #filtersContent>
            <div class="space-y-8">
              <section class="space-y-3">
                <h3 class="text-sm font-semibold text-foreground">
                  {{ t('pages.customer.customerPage.filters.titles.segments') }}
                </h3>
                <MultiSelectAudiences
                  v-if="isFiltersModalOpen"
                  v-model="selectedSegments"
                  :audiences="merchantSegments"
                  :default-filter-placeholder="t('global.multiselect.audiences.segmentsSearchPlaceholder')"
                  :default-empty-filter-message="t('global.multiselect.audiences.noResultSearch')"
                  :default-empty-message="t('global.multiselect.audiences.noResult')"
                  hide-empty-categories
                />
              </section>

              <section class="space-y-4">
                <h3 class="text-sm font-semibold text-foreground">
                  {{ t('pages.customer.customerPage.filters.titles.profile') }}
                </h3>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.genders') }}
                  </legend>
                  <div class="grid grid-cols-3 gap-3">
                    <AppCheckboxField
                      id="man"
                      v-model="filters.genders"
                      value="man"
                      :label="t('pages.customer.customerPage.filters.men')"
                    />
                    <AppCheckboxField
                      id="woman"
                      v-model="filters.genders"
                      value="woman"
                      :label="t('pages.customer.customerPage.filters.women')"
                    />
                    <AppCheckboxField
                      id="unknown"
                      v-model="filters.genders"
                      value="unknown"
                      :label="t('pages.customer.customerPage.filters.unknown')"
                    />
                  </div>
                </fieldset>
                <fieldset class="space-y-4">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.ages') }}
                  </legend>
                  <div class="space-y-2">
                    <Slider
                      :model-value="filters.ages.value"
                      :min="filters.ages.min"
                      :max="filters.ages.max"
                      :step="1"
                      @update:model-value="filtersUpdateAges"
                    />
                    <div class="flex justify-between text-xs text-muted-foreground">
                      <span>{{ filters.ages.value[0] }}</span>
                      <span>{{ filters.ages.value[1] }}</span>
                    </div>
                  </div>
                </fieldset>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.postalCodes') }}
                  </legend>
                  <div
                    class="border-input dark:bg-input/30 flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-control border bg-background px-3 py-1.5 text-sm shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
                    @click="focusPostalCodeInput"
                  >
                    <Badge v-for="code in filters.postalCodes" :key="code" variant="secondary" class="gap-1 pr-1">
                      {{ code }}
                      <button
                        type="button"
                        class="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        :aria-label="t('global.words.clear')"
                        @click.stop="removePostalCode(code)"
                      >
                        <X class="size-3" />
                      </button>
                    </Badge>
                    <input
                      ref="postalCodeFieldRef"
                      v-model="postalCodeInput"
                      type="text"
                      class="placeholder:text-muted-foreground h-7 min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-sm outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                      :placeholder="
                        filters.postalCodes.length === 0
                          ? t('pages.customer.customerPage.filters.postalCodesPlaceholder')
                          : ''
                      "
                      @keydown="onPostalCodeKeydown"
                      @blur="addPostalCodeFromInput"
                    />
                  </div>
                </fieldset>
              </section>

              <section class="space-y-3">
                <h3 class="text-sm font-semibold text-foreground">
                  {{ t('pages.customer.customerPage.filters.titles.engagement') }}
                </h3>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.hasOptin') }}
                  </legend>
                  <div class="grid grid-cols-3 gap-3">
                    <AppCheckboxField
                      id="hasOptin1"
                      v-model="filters.hasOptin"
                      value="1"
                      :label="t('pages.customer.customerPage.filters.optin.1')"
                    />
                    <AppCheckboxField
                      id="hasOptin0"
                      v-model="filters.hasOptin"
                      value="0"
                      :label="t('pages.customer.customerPage.filters.optin.0')"
                    />
                    <AppCheckboxField
                      id="hasOptin2"
                      v-model="filters.hasOptin"
                      value="2"
                      :label="t('pages.customer.customerPage.filters.optin.2')"
                    />
                  </div>
                </fieldset>
              </section>

              <section class="space-y-4">
                <h3 class="text-sm font-semibold text-foreground">
                  {{ t('pages.customer.customerPage.filters.titles.period') }}
                </h3>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.registerDate') }}
                  </legend>
                  <LitepieDatepicker
                    ref="litepieRegisterDate"
                    v-model="filters.registerDate"
                    :formatter="formatter"
                    as-single
                    use-range
                    i18n="fr"
                    @click="showLitepie('register')"
                  />
                </fieldset>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.lastCheckinDate') }}
                  </legend>
                  <LitepieDatepicker
                    ref="litepieLastCheckinDate"
                    v-model="filters.lastCheckinDate"
                    :formatter="formatter"
                    as-single
                    use-range
                    i18n="fr"
                    @click="showLitepie('lastCheckin')"
                  />
                </fieldset>
              </section>

              <section class="space-y-3">
                <h3 class="text-sm font-semibold text-foreground">
                  {{ t('pages.customer.customerPage.filters.titles.source') }}
                </h3>
                <fieldset class="space-y-2">
                  <legend class="text-xs font-medium text-muted-foreground">
                    {{ t('pages.customer.customerPage.filters.channelTags') }}
                  </legend>
                  <div class="flex flex-wrap gap-3">
                    <AppCheckboxField
                      v-for="tag in filterTagBy(tags, 'channel')"
                      :id="`tag_channel_${tag.id}`"
                      :key="tag.id"
                      v-model="filters.tags"
                      :value="tag.id"
                    >
                      <template #label>
                        <BaseChip :color="tag.color" :text="tag.name" />
                      </template>
                    </AppCheckboxField>
                  </div>
                </fieldset>
              </section>
            </div>
          </template>
        </ThePageSettings>

        <div class="mb-6 flex flex-col gap-3 px-6 sm:px-12 md:flex-row md:items-center md:justify-between">
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
            <MultiSelectStores
              v-model="storeFilter"
              :has-label="false"
              class="w-full md:w-72"
              :scoped-store-ids="storesScopedByAudience"
            />
            <Button variant="outline" class="w-full md:w-auto" @click="RefThePageSettings?.handleFiltersModal(true)">
              <SlidersHorizontal />
              {{ t('global.filters.filter') }}
            </Button>
          </div>
          <div class="flex gap-2">
            <div class="relative w-full sm:w-fit">
              <Search
                class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                ref="searchFieldRef"
                v-model="searchText"
                type="search"
                class="pl-10 sm:w-96"
                :placeholder="t('ui.searchPlaceholder')"
                @input="search($event.target.value)"
              />
            </div>
            <Button v-if="displayQrCodeScanner" variant="outline" size="icon" @click="handleScanningSession">
              <QrCode class="h-5 w-5" />
            </Button>
            <CustomerExportCsvButton
              v-if="showUserExportButton && table.rows && table.rows.length > 0"
              icon-only
              path="customers/downloadCsv"
              :params="convertDates(getFiltersParams(), 'DD/MM/YYYY', 'YYYY-MM-DD')"
            />
          </div>
        </div>
        <div v-if="filtered" class="flex flex-wrap gap-2 px-6 pb-4 sm:px-12">
          <BaseChip
            v-for="tag in activeFilterTags"
            :key="tag.key"
            color="violet"
            :text="tag.text"
            removable
            @remove="removeFilter(tag.key)"
          />
        </div>

        <div v-if="table.rows || displayNoResults()" class="px-6 pb-6 sm:px-12 sm:pb-12">
          <div
            v-if="isBulkListRefreshing"
            class="mb-3 flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3"
          >
            <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
            <p class="text-sm font-medium text-foreground">
              {{ t('pages.customer.customerPage.bulkActions.refreshing') }}
            </p>
          </div>
          <AppTable
            ref="customersTable"
            :columns="tableColumns"
            :rows="table.rows"
            :pagination="table.pagination"
            :sort="table.sort"
            :is-loading="!isTableLoaded"
            @update:change-page="changePage"
            @update:change-sort="changeSort"
          >
            <template v-if="canUseBulkActions && hasSelectedCustomers" #toolbar>
              <div
                class="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="flex flex-wrap items-center gap-3">
                  <p class="text-sm font-medium text-foreground">
                    {{
                      t('pages.customer.customerPage.bulkActions.selected', selectedCustomerCount, {
                        count: selectedCustomerCount
                      })
                    }}
                  </p>
                  <Button variant="ghost" size="sm" @click="clearCustomerSelection">
                    {{ t('pages.customer.customerPage.bulkActions.clear') }}
                  </Button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger as-child>
                    <Button variant="outline">
                      {{ t('pages.customer.customerPage.bulkActions.actions') }}
                      <ChevronDown class="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem @click="openBulkAddToListModal">
                      <ListPlus class="h-4 w-4" />
                      {{ t('pages.customer.customerPage.bulkActions.addToList.action') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="openBulkRemoveFromListModal">
                      <ListMinus class="h-4 w-4" />
                      {{ t('pages.customer.customerPage.bulkActions.removeFromList.action') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="openBulkOptinModal">
                      <MessageSquare class="h-4 w-4" />
                      {{ t('pages.customer.customerPage.bulkActions.optin.action') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="openBulkReferenceStoreModal">
                      <MapPin class="h-4 w-4" />
                      {{ t('pages.customer.customerPage.bulkActions.referenceStore.action') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="text-destructive focus:text-destructive"
                      @click="isBulkDeleteModalOpen = true"
                    >
                      <Trash2 class="h-4 w-4" />
                      {{ t('pages.customer.customerPage.bulkActions.delete.action') }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </template>

            <template #empty-state>
              <BaseIcon name="HeroMagnifyingGlassIconOutline" class="h-8 w-8 text-muted-foreground" />
              <p class="mt-2 text-sm font-medium text-foreground-secondary">
                {{ t('pages.customer.emptyState.noResult') }}
              </p>
              <Button variant="outline" class="mt-3 duration-300" @click="resetFilters">
                <BaseIcon name="HeroAdjustmentsVerticalIconSolid" class="h-4 w-4 text-primary" />
                <span class="text-primary">{{ t('pages.customer.customerPage.noResults.reset') }}</span>
              </Button>
            </template>

            <template v-if="canUseBulkActions" #th-selection>
              <div class="flex justify-center" @click.stop>
                <Checkbox
                  :model-value="headerCheckboxState"
                  :aria-label="t('pages.customer.customerPage.bulkActions.selectPage')"
                  @update:model-value="toggleSelectAllOnPage"
                >
                  <Minus v-if="headerCheckboxState === 'indeterminate'" class="size-3.5" />
                  <Check v-else class="size-3.5" />
                </Checkbox>
              </div>
            </template>

            <template v-if="canUseBulkActions" #td-selection="{ row }">
              <div class="flex justify-center" @click.stop>
                <Checkbox
                  :model-value="isCustomerSelected(row.id)"
                  :aria-label="t('pages.customer.customerPage.bulkActions.selectRow')"
                  @update:model-value="(checked) => toggleCustomerSelection(row.id, checked)"
                />
              </div>
            </template>

            <template #td-customer="{ data }">
              <RowCustomer
                :id="data.customer.id"
                :country-flag="data.customer.countryFlag ?? null"
                :email="data.customer.email"
                :loyalty-card-number="data.customer.loyaltyCardNumber"
                :has-optin="data.customer.hasOptin"
                :name="data.customer.name"
                :phone="data.customer.phone"
              />
            </template>

            <template #td-lastCheckin="{ data }">
              <div>
                <div class="text-sm font-medium text-foreground">{{ data.lastCheckin.date }}</div>
                <div v-if="data.lastCheckin.time" class="text-sm text-muted-foreground">
                  {{ data.lastCheckin.time }}
                </div>
              </div>
            </template>

            <template #td-checkins="{ data }">{{ formatNumber(data.checkins.value) }}</template>

            <template #td-points="{ data }">{{ formatNumber(data.points.value) }}</template>

            <template #td-audience="{ data }">
              <div class="flex flex-nowrap items-center gap-2">
                <BaseChip
                  v-for="audience in data.audience.items.slice(0, 2)"
                  :key="audience.id"
                  :text="audience.label"
                  :color="audience.color"
                  :icon="audience.icon"
                />
                <AppTooltip v-if="data.audience.items.length > 2">
                  <BaseChip :text="`+${data.audience.items.length - 2}`" color="slate" />
                  <template #tooltip-content>
                    <div class="flex flex-col gap-1">
                      <span v-for="audience in data.audience.items.slice(2)" :key="audience.id">
                        {{ audience.label }}
                      </span>
                    </div>
                  </template>
                </AppTooltip>
              </div>
            </template>
          </AppTable>
        </div>

        <TheEmptyState
          v-else
          module="customer"
          :primary-button="emptyStateButtons.primaryButton"
          :secondary-button="emptyStateButtons.secondaryButton"
          :title="t('pages.customer.emptyState.title')"
          :description="t('pages.customer.emptyState.description')"
          @on-click="handleClickEmptyState"
        />

        <BaseModal
          :is-open="isCustomerModalOpen"
          :heading-title="t('pages.customer.customerPage.blocks.create.description')"
          @close="resetNewCustomerForm"
        >
          <CustomerModal
            v-model:new-customer="newCustomer"
            v-model:selected-attributes="selectedAttributes"
            v-model:errors="customerErrors"
            :genders-list="gendersList"
            :attributes="merchantAttributes"
            :customer-lists="staticMerchantSegments"
            @load-more-audiences="handleLoadAudiences($event)"
            @search-audience="handleSearchAudience($event)"
            @on-open-import="openImport"
          />
          <template #linkBtn>
            <div class="flex items-center justify-end gap-4">
              <Button
                :disabled="createCustomerIsLoading.create"
                type="button"
                variant="outline"
                @click="addCustomerAndCreateANewOne"
              >
                <Loader2 v-if="createCustomerIsLoading.create" class="animate-spin" />
                {{ t('pages.customer.create.again') }}
              </Button>
              <Button
                type="button"
                variant="default-primary"
                :disabled="createCustomerIsLoading.redirect"
                @click="addCustomerAndRedirect"
              >
                <Loader2 v-if="createCustomerIsLoading.redirect" class="animate-spin" />
                {{ t('global.words.save') }}
              </Button>
            </div>
          </template>
        </BaseModal>
        <BaseModal
          :is-open="showQrCodeScanner"
          :heading-title="t(`pages.customer.customerScanningPage.scan`)"
          @close="handleCloseQrCodeScanner"
        >
          <QrCodeScanner />
        </BaseModal>
        <BaseModal
          :is-open="isBulkDeleteModalOpen"
          :is-processing="isBulkActionLoading"
          :link-text="t('pages.customer.customerPage.bulkActions.delete.confirmButton')"
          :text-title="
            t('pages.customer.customerPage.bulkActions.delete.confirmTitle', selectedCustomerCount, {
              count: selectedCustomerCount
            })
          "
          :text="t('pages.customer.customerPage.bulkActions.delete.confirmDescription')"
          :cancel="t('ui.confirmModal.buttons.cancel')"
          icon="HeroExclamationTriangleIconSolid"
          icon-color="red"
          @click="deleteSelectedCustomers"
          @close="isBulkDeleteModalOpen = false"
        />
        <BaseModal
          :is-open="isBulkOptinModalOpen"
          :heading-title="t('pages.customer.customerPage.bulkActions.optin.title')"
          @close="closeBulkOptinModal"
        >
          <div class="space-y-6 text-left">
            <p class="text-sm text-muted-foreground">
              {{
                t('pages.customer.customerPage.bulkActions.optin.description', selectedCustomerCount, {
                  count: selectedCustomerCount
                })
              }}
            </p>

            <div class="flex w-full flex-col gap-5 sm:flex-row sm:items-start">
              <BulkOptinCombobox
                v-model="bulkSmsOptinId"
                :label="t('pages.customer.customerPage.blocks.create.fields.optin.label')"
                :placeholder="t('pages.customer.customerPage.bulkActions.optin.placeholder')"
                :options="bulkSmsOptinOptions"
              />
              <BulkOptinCombobox
                v-model="bulkEmailOptinId"
                :label="t('pages.customer.customerPage.blocks.create.fields.has_optin_email.label')"
                :placeholder="t('pages.customer.customerPage.bulkActions.optin.placeholder')"
                :options="bulkEmailOptinOptions"
              />
            </div>

            <BulkOptinCombobox
              v-if="hasWallet"
              v-model="bulkPushOptinId"
              :label="t('pages.customer.customerPage.bulkActions.optin.push.label')"
              :placeholder="t('pages.customer.customerPage.bulkActions.optin.placeholder')"
              :options="bulkPushOptinOptions"
            />
          </div>
          <template #linkBtn>
            <div class="flex items-center justify-end gap-4">
              <Button type="button" variant="outline" :disabled="isBulkActionLoading" @click="closeBulkOptinModal">
                {{ t('ui.confirmModal.buttons.cancel') }}
              </Button>
              <Button
                type="button"
                variant="default-primary"
                :disabled="!hasBulkOptinSelection || isBulkActionLoading"
                @click="updateSelectedCustomersOptin"
              >
                <Loader2 v-if="isBulkActionLoading" class="animate-spin" />
                {{ t('pages.customer.customerPage.bulkActions.optin.confirmButton') }}
              </Button>
            </div>
          </template>
        </BaseModal>
        <BaseModal
          :is-open="isBulkAddToListModalOpen"
          :heading-title="t('pages.customer.customerPage.bulkActions.addToList.title')"
          @close="closeBulkAddToListModal"
        >
          <div class="space-y-3 text-left">
            <p class="text-sm text-muted-foreground">
              {{
                t('pages.customer.customerPage.bulkActions.addToList.description', selectedCustomerCount, {
                  count: selectedCustomerCount
                })
              }}
            </p>
            <div>
              <label class="mb-1 block text-sm font-medium text-foreground">
                {{ t('pages.customer.customerPage.bulkActions.addToList.inputLabel') }}
              </label>
              <DropdownAudience
                v-model="bulkListSegmentId"
                :audiences="staticMerchantSegments"
                type="static"
                creation-allowed
                :placeholder="t('global.multiselect.audiences.placeholderCreateAllowed')"
                :filter-placeholder="t('global.multiselect.audiences.filterPlaceholderCreateAllowed')"
                :default-empty-message="t('global.multiselect.audiences.noLists')"
                @audience-created="refreshStaticMerchantSegments"
              />
            </div>
          </div>
          <template #linkBtn>
            <div class="flex items-center justify-end gap-4">
              <Button type="button" variant="outline" :disabled="isBulkActionLoading" @click="closeBulkAddToListModal">
                {{ t('ui.confirmModal.buttons.cancel') }}
              </Button>
              <Button
                type="button"
                variant="default-primary"
                :disabled="!bulkListSegmentId || isBulkActionLoading"
                @click="addSelectedCustomersToList"
              >
                <Loader2 v-if="isBulkActionLoading" class="animate-spin" />
                {{ t('pages.customer.customerPage.bulkActions.addToList.confirmButton') }}
              </Button>
            </div>
          </template>
        </BaseModal>
        <BaseModal
          :is-open="isBulkRemoveFromListModalOpen"
          :heading-title="t('pages.customer.customerPage.bulkActions.removeFromList.title')"
          @close="closeBulkRemoveFromListModal"
        >
          <div class="space-y-3 text-left">
            <p class="text-sm text-muted-foreground">
              {{
                t('pages.customer.customerPage.bulkActions.removeFromList.description', selectedCustomerCount, {
                  count: selectedCustomerCount
                })
              }}
            </p>
            <div>
              <label class="mb-1 block text-sm font-medium text-foreground">
                {{ t('pages.customer.customerPage.bulkActions.removeFromList.inputLabel') }}
              </label>
              <DropdownAudience
                v-model="bulkRemoveListSegmentId"
                :audiences="staticMerchantSegments"
                type="static"
                :placeholder="t('global.multiselect.audiences.selectList')"
                :filter-placeholder="t('global.multiselect.audiences.listsSearchPlaceholder')"
                :default-empty-message="t('global.multiselect.audiences.noLists')"
              />
            </div>
          </div>
          <template #linkBtn>
            <div class="flex items-center justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                :disabled="isBulkActionLoading"
                @click="closeBulkRemoveFromListModal"
              >
                {{ t('ui.confirmModal.buttons.cancel') }}
              </Button>
              <Button
                type="button"
                variant="default-primary"
                :disabled="!bulkRemoveListSegmentId || isBulkActionLoading"
                @click="removeSelectedCustomersFromList"
              >
                <Loader2 v-if="isBulkActionLoading" class="animate-spin" />
                {{ t('pages.customer.customerPage.bulkActions.removeFromList.confirmButton') }}
              </Button>
            </div>
          </template>
        </BaseModal>
        <BaseModal
          :is-open="isBulkReferenceStoreModalOpen"
          :heading-title="t('pages.customer.customerPage.bulkActions.referenceStore.title')"
          @close="closeBulkReferenceStoreModal"
        >
          <div class="space-y-3 text-left">
            <p class="text-sm text-muted-foreground">
              {{
                t('pages.customer.customerPage.bulkActions.referenceStore.description', selectedCustomerCount, {
                  count: selectedCustomerCount
                })
              }}
            </p>
            <ComboboxSelect
              v-model="bulkReferenceStoreId"
              value-key="id"
              label-key="text"
              :placeholder="t('pages.customer.customerPage.blocks.create.fields.chooseStore')"
              :options="bulkReferenceStoreOptions"
              trigger-class="w-full"
            />
          </div>
          <template #linkBtn>
            <div class="flex items-center justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                :disabled="isBulkActionLoading"
                @click="closeBulkReferenceStoreModal"
              >
                {{ t('ui.confirmModal.buttons.cancel') }}
              </Button>
              <Button
                type="button"
                variant="default-primary"
                :disabled="!bulkReferenceStoreId || isBulkActionLoading"
                @click="updateSelectedCustomersReferenceStore"
              >
                <Loader2 v-if="isBulkActionLoading" class="animate-spin" />
                {{ t('pages.customer.customerPage.bulkActions.referenceStore.confirmButton') }}
              </Button>
            </div>
          </template>
        </BaseModal>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
